import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { keepPreviousData } from '@tanstack/react-query';
import {
  createTasks,
  getCurrentUser,
  getTask,
  getTaskFacets,
  listLabels,
  listTasks,
  listTenantUsers,
  updateTask,
} from '@/services/DataService';
import type {
  Task,
  TaskCreateInput,
  TaskListQuery,
  TaskListResponse,
  TaskPatch,
} from '@/types/task';

export const TASKS_KEY = ['tasks'] as const;
export const TASK_FACETS_KEY = ['task-facets'] as const;
export const LABELS_KEY = ['labels'] as const;
export const TENANT_USERS_KEY = ['tenant-users'] as const;
export const CURRENT_USER_KEY = ['current-user'] as const;

/** The cache key for one page of the board, which is one server answer. */
export const tasksKey = (query: TaskListQuery) => ['tasks', query] as const;

/** The detail cache key for one task. */
export const taskKey = (taskId: string) => ['task', taskId];

/**
 * Reads a page of the board.
 *
 * The server does the filtering, sorting and searching, so what comes back is
 * the page as asked for and the total behind it. Between two pages the previous
 * one stays on screen rather than blanking, which is what makes paging and
 * re-sorting read as the same table rather than a reload.
 */
export const useTasks = (query: TaskListQuery) =>
  useQuery({
    queryKey: tasksKey(query),
    queryFn: () => listTasks(query),
    placeholderData: keepPreviousData,
  });

/**
 * Reads one task, painting immediately from whatever page is already loaded
 * while the fetch that confirms it is in flight.
 */
export const useTask = (taskId: string | null) => {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: taskKey(taskId ?? ''),
    queryFn: () => getTask(taskId as string),
    enabled: Boolean(taskId),
    placeholderData: () => {
      const pages = queryClient.getQueriesData<{ tasks: Task[] }>({ queryKey: TASKS_KEY });

      for (const [ , page ] of pages) {
        const found = page?.tasks.find((task) => task.id === taskId);

        if (found) {
          return found;
        }
      }

      return undefined;
    },
  });
};

/**
 * The counts the filter menu puts beside each value.
 *
 * Refetched whenever the board is written to, because a filter that says
 * "3 tasks" next to a value that now holds four is worse than no count at all.
 */
export const useTaskFacets = () =>
  useQuery({ queryKey: TASK_FACETS_KEY, queryFn: getTaskFacets });

/** The workspace's labels, which the picker offers before it creates a new one. */
export const useLabels = () =>
  useQuery({ queryKey: LABELS_KEY, queryFn: listLabels });

export const useTenantUsers = () =>
  useQuery({ queryKey: TENANT_USERS_KEY, queryFn: listTenantUsers });

/** The signed-in person, which is how "Assigned to me" knows whose tasks to ask for. */
export const useCurrentUser = () =>
  useQuery({ queryKey: CURRENT_USER_KEY, queryFn: getCurrentUser, staleTime: Infinity });

/**
 * Fields a patch and a task spell the same way, and so can be painted straight
 * from the patch. The others cannot: a patch carries `assignee_user_id` where a
 * task carries a person, and label names where a task carries label rows.
 * Spreading those would put wire shapes into the cache and blank the very
 * fields the paint exists to keep steady.
 */
const DIRECTLY_PAINTABLE = [
  'title',
  'description',
  'status',
  'start_date',
  'due_date',
] as const;

/**
 * What can be shown immediately, from the patch plus whatever the caller
 * resolved for it. Anything else waits for the server's answer, which is a
 * moment away and correct.
 */
function paintableChanges (patch: TaskPatch, resolved?: Partial<Task>): Partial<Task> {
  const changes: Partial<Task> = {};

  DIRECTLY_PAINTABLE.forEach((field) => {
    if (field in patch) {
      Object.assign(changes, { [field]: patch[field] });
    }
  });

  return { ...changes, ...resolved };
}

/** Applies an edit to whichever loaded pages carry the task. */
function paintPages (queryClient: QueryClient, taskId: string, changes: Partial<Task>) {
  queryClient.setQueriesData<TaskListResponse>(
    { queryKey: TASKS_KEY },
    (page) => (page
      ? {
        ...page,
        tasks: page.tasks.map((task) => (task.id === taskId ? { ...task, ...changes } : task)),
      }
      : page),
  );
}

/**
 * Changes a task, painting the change before the server confirms it.
 *
 * `optimistic` carries the parts of the answer a patch cannot imply — the
 * person behind an id — so the caller that knows them supplies them and the
 * panel does not blank a field for a moment.
 */
export const useUpdateTask = () => {
  const queryClient = useQueryClient();

  return useMutation({
    // Task writes run one at a time. Concurrent edits would each snapshot a
    // cache the other had already painted, so one failure could revert an
    // unrelated success, and responses landing out of order could put an older
    // task back over a newer one.
    scope: { id: 'task-update' },
    mutationFn: ({ taskId, patch }: {
      taskId: string;
      patch: TaskPatch;
      optimistic?: Partial<Task>;
    }) => updateTask(taskId, patch),
    onMutate: async ({ taskId, patch, optimistic }) => {
      const detailKey = taskKey(taskId);

      await Promise.all([
        queryClient.cancelQueries({ queryKey: TASKS_KEY }),
        queryClient.cancelQueries({ queryKey: detailKey }),
      ]);

      const previousPages = queryClient.getQueriesData<TaskListResponse>({ queryKey: TASKS_KEY });
      const previousDetail = queryClient.getQueryData<Task>(detailKey);

      paintPages(queryClient, taskId, paintableChanges(patch, optimistic));
      queryClient.setQueryData<Task>(detailKey, (old) =>
        (old ? { ...old, ...paintableChanges(patch, optimistic) } : old));

      return { previousPages, previousDetail, detailKey };
    },
    onError: (_error, _variables, context) => {
      context?.previousPages.forEach(([ key, page ]) =>
        queryClient.setQueryData<TaskListResponse>(key, page));

      if (context?.previousDetail) {
        queryClient.setQueryData(context.detailKey, context.previousDetail);
      }
    },
    onSuccess: (task) => {
      queryClient.setQueryData<Task>(taskKey(task.id), task);
      paintPages(queryClient, task.id, task);
    },
    onSettled: () => {
      // An edit can move a task out of the page it was on — a status filter, a
      // sort on the field just changed — which only the server can decide. The
      // counts behind the filter menu move with it.
      queryClient.invalidateQueries({ queryKey: TASKS_KEY });
      queryClient.invalidateQueries({ queryKey: TASK_FACETS_KEY });
      queryClient.invalidateQueries({ queryKey: LABELS_KEY });
    },
  });
};

export const useCreateTasks = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (tasks: TaskCreateInput[]) => createTasks(tasks),
    onSuccess: (created) => {
      created.forEach((task) => queryClient.setQueryData<Task>(taskKey(task.id), task));
      queryClient.invalidateQueries({ queryKey: TASKS_KEY });
      queryClient.invalidateQueries({ queryKey: TASK_FACETS_KEY });
      queryClient.invalidateQueries({ queryKey: LABELS_KEY });
    },
  });
};

/** The chat tools that can change a task behind the panel's back. */
const TASK_WRITING_TOOLS = [ 'task_create', 'task_update' ];

/**
 * Refreshes what the board is showing after the agent has written to it, so a
 * task edited by asking in chat updates the panel someone is looking at.
 *
 * Gated on the tools that actually write: invalidating after every tool round
 * would refetch the whole board because an unrelated chart was drawn.
 */
export const invalidateTaskQueries = (queryClient: QueryClient, executedToolNames: string[]) => {
  if (!executedToolNames.some((name) => TASK_WRITING_TOOLS.includes(name))) {
    return;
  }

  queryClient.invalidateQueries({ queryKey: TASKS_KEY });
  queryClient.invalidateQueries({ queryKey: [ 'task' ] });
  queryClient.invalidateQueries({ queryKey: TASK_FACETS_KEY });
  queryClient.invalidateQueries({ queryKey: LABELS_KEY });
};
